import { TrackedEvents, trackEvent } from 'analytics';
import { type DetectedUbcContent, Evidence, useApiTradeV1SiteMaterialsEvidenceList } from 'api';
import { dayjs } from 'date';
import { Trans, useTranslation } from 'i18n';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Accordion,
  AccordionSummary,
  Alert,
  Box,
  CenteredLoader,
  Chip,
  DataCard,
  ExpandableIconButton,
  formatNumber,
  Grid,
  MediaDialog,
  ProgressiveImage,
  Tab,
  Tabs,
  Typography,
} from 'ui';
import { ExpandMore, ImageOutlined, Scale } from 'ui/icons';

import {
  AiFeedbackVisibilityProvider,
  AiFiltersAndBreakdownSidebar,
  AnnotatedMediaItem,
  useAnnotations,
} from '../../../annotations';
import { getMaterialCategoryFromEvaluatedSpecs } from '../../../breakdowns/utils/getMaterialCategoryFromEvaluatedSpecs/getMaterialCategoryFromEvaluatedSpecs';
import { TargetBaleWeightsHistogram } from '../../../charts';

type TabValue = 'typical' | 'best';

const MAX_IMAGE_SHOWN = 3;
const model = 'alu';
const RANGE_EQUAL_TOLERANCE = 0.0001;

const PERCENT_FORMAT_OPTIONS = {
  maximumFractionDigits: 0,
  minimumFractionDigits: 0,
  style: 'percent' as const,
};

export const getRangeDisplay = (low: number, high: number) => {
  const formattedLow = formatNumber(low * 100, PERCENT_FORMAT_OPTIONS);
  const formattedHigh = formatNumber(high * 100, PERCENT_FORMAT_OPTIONS);

  const rangesEqual = Math.abs(low - high) < RANGE_EQUAL_TOLERANCE;

  return rangesEqual ? formattedLow : `${formattedLow} - ${formattedHigh}`;
};

const getTargetGrade = (grade: string): number => {
  return Number(grade.split('/')[0]);
};

const Callout = ({
  percentage,
  title,
  variant = 'primary',
}: {
  percentage: string;
  title: string;
  variant: 'primary' | 'secondary';
}) => {
  return (
    <Alert
      severity="info"
      icon={false}
      sx={(theme) => ({
        backgroundColor:
          variant === 'primary' ? `${theme.palette.primary.main}15` : `${theme.palette.secondary.main}05`,
      })}
    >
      <Typography
        variant="h5"
        fontWeight="bold"
        sx={[
          (theme) => ({
            color: variant === 'primary' ? theme.palette.primary.main : theme.palette.secondary.main,
          }),
        ]}
      >
        {percentage}
      </Typography>
      <Typography
        variant="body1"
        sx={(theme) => ({
          color: variant === 'primary' ? theme.palette.primary.main : theme.palette.secondary.main,
        })}
      >
        {title}
      </Typography>
    </Alert>
  );
};

export const TargetBaleWeightsDataCard = ({
  isLoading,
  detectedUbcContent,
  grade,
  siteMaterialId,
}: {
  isLoading?: boolean;
  detectedUbcContent: DetectedUbcContent;
  grade: string;
  siteMaterialId: string;
}) => {
  const { t } = useTranslation('features');
  const targetGrade = getTargetGrade(grade);
  const today = dayjs();
  const ninetyDaysAgo = today.subtract(90, 'days');
  const [activeTab, setActiveTab] = useState<TabValue>('typical');

  const { data: baleEvidence, isLoading: isEvidenceLoading } = useApiTradeV1SiteMaterialsEvidenceList(
    siteMaterialId,
    {
      include_ids:
        activeTab === 'typical'
          ? detectedUbcContent.typical_load_evidence_ids
          : detectedUbcContent.best_load_evidence_ids,
      include_self_serve_sessions: true,
    },
    {
      query: {
        enabled:
          Boolean(siteMaterialId) && activeTab === 'typical'
            ? detectedUbcContent.typical_load_evidence_ids.length > 0
            : detectedUbcContent.best_load_evidence_ids.length > 0,
      },
    }
  );

  const handleChangeTab = (_event: React.SyntheticEvent, value: TabValue) => {
    setActiveTab(value);
  };

  const averageUbcContent = formatNumber(detectedUbcContent.average_ubc_content * 100, PERCENT_FORMAT_OPTIONS);
  const attachmentContent = formatNumber(100 - detectedUbcContent.average_ubc_content * 100, PERCENT_FORMAT_OPTIONS);
  const {
    selectedAnnotation,
    handleImageChange,
    hiddenCategories,
    handleHideCategory,
    handleToggleLegend,
    showLegend,
  } = useAnnotations({ model });

  const [selectedEvidence, setSelectedEvidence] = useState<Evidence | null>(null);

  const carouselMedia = baleEvidence?.results.map((evidence) => String(evidence.file || evidence.preview)) ?? [];

  const handleSelect = (src: string) => {
    const relatedMedia = baleEvidence?.results.find((file) => file.file === src || file.preview === src);
    setSelectedEvidence(relatedMedia || null);
    handleImageChange([...(relatedMedia?.model_results?.coco_prediction || [])]);
  };

  const navigate = useNavigate();
  const handleClickMedia = () => {
    trackEvent(TrackedEvents.QUALITY_REPORT_CLICK_CATEGORY_IMAGES, {
      category: 'AI Bale',
    });
    navigate('media?tab=accepted');
  };

  const aluminium_keys = [
    {
      label: t('dataCards.targetBaleWeights.attachmentDetails.otherAluminium'),
      value:
        detectedUbcContent.aluminium_composition.other_aluminium +
        detectedUbcContent.aluminium_composition.aluminium_aerosol,
    },
    {
      label: t('dataCards.targetBaleWeights.attachmentDetails.foodCan'),
      value: detectedUbcContent.aluminium_composition.food_can,
    },
    {
      label: t('dataCards.targetBaleWeights.attachmentDetails.otherMetal'),
      value: detectedUbcContent.aluminium_composition.other_metal,
    },
    {
      label: t('dataCards.targetBaleWeights.attachmentDetails.nonMetal'),
      value: detectedUbcContent.aluminium_composition.non_metal,
    },
  ];

  return (
    <>
      <DataCard
        isLoading={isLoading}
        slotProps={{
          header: {
            action: (
              <ExpandableIconButton
                icon={<ImageOutlined />}
                label={t('dataCards.targetBaleWeights.actions.showMedia')}
                onClick={handleClickMedia}
              />
            ),
            avatar: <Scale />,
            title: t('dataCards.targetBaleWeights.title'),
          },
        }}
        dataTestId="target-bale-weights-data-card"
      >
        <DataCard.Item colSpan={6}>
          <Callout
            percentage={averageUbcContent}
            title={t('dataCards.targetBaleWeights.callouts.averageUbcWeightContent')}
            variant="primary"
          />
        </DataCard.Item>
        <DataCard.Item colSpan={6}>
          <Callout
            percentage={attachmentContent}
            title={t('dataCards.targetBaleWeights.callouts.attachmentWeightContent')}
            variant="secondary"
          />
        </DataCard.Item>
        <DataCard.Item colSpan={12}>
          <Typography color="secondary">
            <Trans
              namespace="features"
              i18nKey="dataCards.targetBaleWeights.inspectorsGatheredMessage"
              values={{
                count: detectedUbcContent.target_bale_weights.length,
                from: ninetyDaysAgo.format('MMMM YYYY'),
                to: today.format('MMMM YYYY'),
              }}
              components={{
                bold: <strong />,
              }}
            />
          </Typography>
        </DataCard.Item>
        <DataCard.Item colSpan={12}>
          <Accordion defaultExpanded={true}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="subtitle2" fontWeight="bold">
                {t('dataCards.targetBaleWeights.attachmentDetails.title')}
              </Typography>
            </AccordionSummary>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                padding: 2,
              }}
            >
              {aluminium_keys.map(({ label, value }) => (
                <Chip
                  key={label}
                  label={`${label} ${formatNumber(Number(value) * 100, {
                    maximumFractionDigits: 2,
                    minimumFractionDigits: 2,
                    style: 'percent',
                  })}`}
                  size="small"
                />
              ))}
            </Box>
          </Accordion>
        </DataCard.Item>
        <DataCard.Item colSpan={12}>
          <Accordion defaultExpanded={false}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="subtitle2" fontWeight="bold">
                {t('dataCards.targetBaleWeights.showBaleQualityData')}
              </Typography>
            </AccordionSummary>
            <Box
              sx={{
                padding: 2,
              }}
            >
              <TargetBaleWeightsHistogram
                targetBaleWeights={detectedUbcContent.target_bale_weights}
                targetGrade={targetGrade}
              />
            </Box>
          </Accordion>
        </DataCard.Item>
        <DataCard.Item colSpan={12}>
          <Box sx={{ borderBottom: 2, borderColor: 'divider' }}>
            <Tabs value={activeTab} onChange={handleChangeTab}>
              <Tab
                label={
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {t('dataCards.targetBaleWeights.tabs.typicalBales')}
                    </Typography>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {`${getRangeDisplay(
                        detectedUbcContent.typical_load_low_range,
                        detectedUbcContent.typical_load_high_range
                      )} ${t('dataCards.targetBaleWeights.tabs.ubcSuffix')}`}
                    </Typography>
                  </Box>
                }
                sx={{
                  textTransform: 'none',
                }}
                value="typical"
                wrapped
              />
              <Tab
                label={
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {t('dataCards.targetBaleWeights.tabs.bestBales')}
                    </Typography>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {getRangeDisplay(detectedUbcContent.best_load_low_range, detectedUbcContent.best_load_high_range)}
                    </Typography>
                  </Box>
                }
                sx={{
                  textTransform: 'none',
                }}
                value="best"
                wrapped
              />
            </Tabs>
          </Box>
        </DataCard.Item>
        <DataCard.Item colSpan={12}>
          <Grid container>
            {isEvidenceLoading ? (
              <Grid size={12}>
                <CenteredLoader />
              </Grid>
            ) : null}
            {baleEvidence?.results.map((evidence, index) => {
              if (index >= MAX_IMAGE_SHOWN) return null;

              return (
                <Grid key={index} size={4}>
                  <ProgressiveImage
                    key={index}
                    rounded={true}
                    src={String(evidence.file)}
                    alt={t('dataCards.targetBaleWeights.evidencePhotoAlt')}
                    loading="lazy"
                    placeholderSrc={String(evidence.preview || evidence.file)}
                    onClick={() => handleSelect(String(evidence.file))}
                    zoomOnHover={true}
                    sx={{
                      cursor: 'pointer',
                    }}
                  />
                </Grid>
              );
            })}
          </Grid>
        </DataCard.Item>
      </DataCard>
      <AiFeedbackVisibilityProvider>
        <MediaDialog
          open={Boolean(selectedEvidence)}
          selectedMedium={selectedEvidence?.file || ''}
          onClose={() => setSelectedEvidence(null)}
          media={carouselMedia}
          handleNavigate={handleSelect}
          imageCount={carouselMedia.length}
          slotProps={{
            imageCarousel: {
              slotProps: {
                ...(selectedAnnotation && selectedEvidence
                  ? {
                      mediaItemSlot: (
                        <AnnotatedMediaItem
                          image={selectedEvidence.file || ''}
                          annotations={selectedAnnotation}
                          hiddenCategories={hiddenCategories}
                          showLegend={showLegend}
                          model={model}
                          showAerosols={false}
                          evidenceId={selectedEvidence.id}
                        />
                      ),
                      sidebar: {
                        children: (
                          <AiFiltersAndBreakdownSidebar
                            selectedAnnotation={selectedAnnotation}
                            hiddenCategories={hiddenCategories}
                            evidenceId={selectedEvidence.id}
                            handleHideCategory={handleHideCategory}
                            handleToggleLegend={handleToggleLegend}
                            showLegend={showLegend}
                            model={model}
                            aluminiumComposition={selectedEvidence.aluminium_composition ?? undefined}
                            hdpeComposition={selectedEvidence.hdpe_composition ?? undefined}
                            petComposition={
                              selectedEvidence.pet_composition ?? selectedEvidence.pet_loose_composition ?? undefined
                            }
                            evaluatedSpecs={selectedEvidence.evaluated_specs ?? undefined}
                            materialCategory={getMaterialCategoryFromEvaluatedSpecs(selectedEvidence.evaluated_specs)}
                            showAerosols={false}
                          />
                        ),
                      },
                    }
                  : null),
              },
            },
          }}
        />
      </AiFeedbackVisibilityProvider>
    </>
  );
};
